import { useTranslationCapManage } from '@dealstack/frontend/shared/mechanism/i18n/i18nHooks';
import { NumberContainer } from './styled';
import { useMemo } from 'react';
import { UploadStatItem } from '@dealstack/frontend/shared/components/Documents/UploadDocuments/UploadStats/UploadStatItem';
import { FileWithClassifier } from '@dealstack/frontend/shared/components/Documents/UploadDocuments/AddFileStep/types';
import { DuplicateFile } from '@dealstack/frontend/shared/components/Documents/types';
import { FindRelatedFilesRes } from '@dealstack/shared/lib/generated';
import {
	DOCUMENT_LIBRARY_ELEMENT_ID,
	makeComplexDataTestId,
} from '@dealstack/shared/tests/testElementIds';

interface Props {
	uploadFileData: FileWithClassifier[];
	localDuplicateFiles: {
		filesWithDuplicates: DuplicateFile[];
		totalDuplicates: number;
	};
	relatedFilesInDeal: FindRelatedFilesRes[] | undefined;
}

export const UploadStats = ({ uploadFileData, localDuplicateFiles, relatedFilesInDeal }: Props) => {
	const tCapManage = useTranslationCapManage();
	const totalRelatedTransactionsToLink =
		relatedFilesInDeal?.reduce((acc, file) => {
			const { relatedTransactions } = file;
			const shouldLink = relatedTransactions.filter(
				(related) => related.shouldLink === true
			).length;

			return acc + shouldLink;
		}, 0) ?? 0;

	// without this the user has no explanation for a batch that will upload nothing.
	const totalFilesAlreadyInDeal =
		relatedFilesInDeal?.filter((file) => file.matchedDocuments.length > 0).length ?? 0;

	const itemStatsInfo = useMemo(
		() => [
			{
				statKey: 'uploaded',
				color: '#4F616C',
				itemHeader: tCapManage(
					'document.upload_state.uploaded_section.footer.uploaded.header'
				),
				itemNumber: uploadFileData.length,
				itemSubHeader: tCapManage(
					'document.upload_state.uploaded_section.footer.uploaded.sub_header'
				),
			},
			...(localDuplicateFiles.totalDuplicates > 0
				? [
						{
							statKey: 'unique',
							color: '#00A36D',
							itemHeader: tCapManage(
								'document.upload_state.uploaded_section.footer.unique.header'
							),
							itemNumber: uploadFileData.length - localDuplicateFiles.totalDuplicates,
							itemSubHeader: tCapManage(
								'document.upload_state.uploaded_section.footer.unique.sub_header'
							),
						},
					]
				: []),
			...(localDuplicateFiles.totalDuplicates > 0
				? [
						{
							statKey: 'duplicates',
							color: '#F76E80',
							itemHeader: tCapManage(
								'document.upload_state.uploaded_section.footer.duplicates.header'
							),
							itemNumber: localDuplicateFiles.totalDuplicates,
							itemSubHeader: tCapManage(
								'document.upload_state.uploaded_section.footer.duplicates.sub_header'
							),
						},
					]
				: []),
			...(totalFilesAlreadyInDeal > 0
				? [
						{
							statKey: 'already_in_deal',
							color: '#F5A623',
							itemHeader: tCapManage(
								'document.upload_state.uploaded_section.footer.already_in_deal.header'
							),
							itemNumber: totalFilesAlreadyInDeal,
							itemSubHeader: tCapManage(
								'document.upload_state.uploaded_section.footer.already_in_deal.sub_header'
							),
						},
					]
				: []),
			...(totalRelatedTransactionsToLink > 0
				? [
						{
							statKey: 'linked',
							color: '#0D5EF2',
							itemHeader: tCapManage(
								'document.upload_state.uploaded_section.footer.linked.header'
							),
							itemNumber: totalRelatedTransactionsToLink,
							itemSubHeader: tCapManage(
								'document.upload_state.uploaded_section.footer.linked.sub_header'
							),
						},
					]
				: []),
		],
		[
			tCapManage,
			uploadFileData.length,
			localDuplicateFiles.totalDuplicates,
			totalFilesAlreadyInDeal,
			totalRelatedTransactionsToLink,
		]
	);
	return (
		<NumberContainer>
			{itemStatsInfo.map((item, index) => (
				<UploadStatItem
					key={`${item.itemHeader}_${index}`}
					dataTestId={makeComplexDataTestId(
						DOCUMENT_LIBRARY_ELEMENT_ID.UPLOAD_STAT_VALUE,
						item.statKey
					)}
					color={item.color}
					itemHeader={item.itemHeader}
					itemNumber={item.itemNumber}
					itemSubHeader={item.itemSubHeader}
				/>
			))}
		</NumberContainer>
	);
};
